import type { Declaration, GlossaProgram, LValue, Statement } from '../parser/ast.js';

/**
 * Δεν επιτρέπεται ανάθεση σε ΟΛΟΚΛΗΡΟ πίνακα (χωρίς δείκτη) — μόνο στα
 * μεμονωμένα στοιχεία του. Βλ. `Interpreter.requireNotWholeArrayAssignment`
 * για το runtime αντίστοιχο. Αυτός ο έλεγχος τρέχει ΠΡΙΝ την εκτέλεση (ίδια
 * λογική με `findConstantReassignment`) ώστε να πιάνει το λάθος ΑΚΟΜΑ ΚΙ αν
 * βρίσκεται σε κλάδο/βρόχο που δεν θα εκτελούνταν σε αυτό το συγκεκριμένο
 * τρέξιμο.
 *
 * Μόνο για ΠΛΗΡΕΣ πρόγραμμα ΓΛΩΣΣΑΣ — απαιτεί ρητές δηλώσεις για να ξέρουμε
 * ΣΤΑΤΙΚΑ ποια ονόματα είναι πίνακες. Στην Ψευδογλώσσα (όπου το μέγεθος
 * ενός πίνακα συχνά συνάγεται δυναμικά από βρόχους) μένει μόνο το runtime
 * check — "απόρριψη αντί για μάντεμα".
 */
export interface WholeArrayAssignment {
  readonly name: string;
  readonly line: number;
  readonly column: number;
}

function findDeclaredKind(declarations: readonly Declaration[], name: string): { readonly isArray: boolean } | undefined {
  for (const decl of declarations) {
    if (decl.kind === 'ConstantDeclaration') {
      if (decl.name === name) return { isArray: false };
      continue;
    }
    for (const declarator of decl.declarators) {
      if (declarator.name === name) return { isArray: declarator.dimensions.length > 0 };
    }
  }
  return undefined;
}

function checkTarget(target: LValue, declarations: readonly Declaration[]): WholeArrayAssignment | null {
  if (target.kind !== 'Identifier') return null;
  const info = findDeclaredKind(declarations, target.name);
  if (info === undefined || !info.isArray) return null;
  return { name: target.name, line: target.line, column: target.column };
}

function checkStatement(stmt: Statement, declarations: readonly Declaration[]): WholeArrayAssignment | null {
  switch (stmt.kind) {
    case 'AssignmentStatement':
      return checkTarget(stmt.target, declarations);
    case 'ReadStatement': {
      for (const target of stmt.targets) {
        const result = checkTarget(target, declarations);
        if (result !== null) return result;
      }
      return null;
    }
    case 'SwapStatement': {
      const left = checkTarget(stmt.left, declarations);
      if (left !== null) return left;
      return checkTarget(stmt.right, declarations);
    }
    case 'ForStatement':
      return checkStatements(stmt.body, declarations);
    case 'IfStatement': {
      const thenResult = checkStatements(stmt.thenBranch, declarations);
      if (thenResult !== null) return thenResult;
      for (const branch of stmt.elseIfBranches) {
        const branchResult = checkStatements(branch.body, declarations);
        if (branchResult !== null) return branchResult;
      }
      if (stmt.elseBranch !== null) return checkStatements(stmt.elseBranch, declarations);
      return null;
    }
    case 'WhileStatement':
      return checkStatements(stmt.body, declarations);
    case 'RepeatUntilStatement':
      return checkStatements(stmt.body, declarations);
    case 'SwitchStatement': {
      for (const switchCase of stmt.cases) {
        const result = checkStatements(switchCase.body, declarations);
        if (result !== null) return result;
      }
      if (stmt.elseBody !== null) return checkStatements(stmt.elseBody, declarations);
      return null;
    }
    case 'WriteStatement':
    case 'CallStatement':
      return null;
  }
}

function checkStatements(statements: readonly Statement[], declarations: readonly Declaration[]): WholeArrayAssignment | null {
  for (const stmt of statements) {
    const result = checkStatement(stmt, declarations);
    if (result !== null) return result;
  }
  return null;
}

/**
 * Επιστρέφει την ΠΡΩΤΗ ανάθεση σε ολόκληρο πίνακα (με σειρά εμφάνισης —
 * κύριο πρόγραμμα πρώτα, μετά κάθε υποπρόγραμμα), ή `null` αν δεν υπάρχει
 * καμία. Κάθε υποπρόγραμμα ελέγχεται με τις ΔΙΚΕΣ ΤΟΥ δηλώσεις (αυστηρά
 * τοπική εμβέλεια — βλ. `findUndeclaredGlossaReference`).
 */
export function findWholeArrayAssignment(program: GlossaProgram): WholeArrayAssignment | null {
  const mainResult = checkStatements(program.body, program.declarations);
  if (mainResult !== null) return mainResult;

  for (const sub of program.subprograms) {
    const subResult = checkStatements(sub.body, sub.declarations);
    if (subResult !== null) return subResult;
  }
  return null;
}
